/**
 * Developer Apps management page.
 *
 * Each app gets a unique clientId used with connectViaPortal({ clientId }).
 * One developer account ↔ many apps.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { getApps, createApp, deleteApp, updateApp } from '../services';
import { DeveloperApp } from '../types';
import {
  Plus,
  Trash2,
  Copy,
  CheckCircle2,
  AppWindow,
  X,
  Edit2,
  Check,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return '—';
  }
}

// ── Component ─────────────────────────────────────────────────────────────

export default function AppsPage() {
  const [apps, setApps] = useState<DeveloperApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  // Create form state
  const [appName, setAppName] = useState('');
  const [originsInput, setOriginsInput] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Copy feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Editing origins
  const [editingOrigin, setEditingOrigin] = useState<{ clientId: string; index: number; value: string } | null>(null);
  const [newOriginInput, setNewOriginInput] = useState<{ [clientId: string]: string }>({});

  const load = useCallback(() => {
    setLoadError(null);
    getApps()
      .then(setApps)
      .catch((err: Error) => setLoadError(err.message || 'Failed to load apps.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const handleCreate = async () => {
    setCreateError(null);
    if (!appName.trim()) {
      setCreateError('App name is required.');
      return;
    }

    const allowedOrigins = originsInput
      .split(',')
      .map((o) => o.trim())
      .filter(Boolean);

    setCreating(true);
    try {
      await createApp(appName.trim(), allowedOrigins);
      setShowCreate(false);
      setAppName('');
      setOriginsInput('');
      load();
    } catch (err) {
      setCreateError((err as Error).message || 'Failed to create app.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (clientId: string) => {
    if (!window.confirm('Delete this app? This cannot be undone.')) return;
    try {
      await deleteApp(clientId);
      load();
    } catch (err) {
      alert((err as Error).message || 'Failed to delete app.');
    }
  };

  const handleCopy = (clientId: string) => {
    navigator.clipboard.writeText(clientId).then(() => {
      setCopiedId(clientId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleDeleteOrigin = async (clientId: string, originToDelete: string) => {
    const app = apps.find((a) => a.clientId === clientId);
    if (!app) return;

    const updatedOrigins = app.allowedOrigins.filter((o) => o !== originToDelete);
    try {
      await updateApp(clientId, { allowedOrigins: updatedOrigins });
      load();
    } catch (err) {
      alert((err as Error).message || 'Failed to delete origin.');
    }
  };

  const handleStartEditOrigin = (clientId: string, index: number, currentValue: string) => {
    setEditingOrigin({ clientId, index, value: currentValue });
  };

  const handleSaveEditOrigin = async () => {
    if (!editingOrigin) return;
    
    const app = apps.find((a) => a.clientId === editingOrigin.clientId);
    if (!app) return;

    const updatedOrigins = [...app.allowedOrigins];
    updatedOrigins[editingOrigin.index] = editingOrigin.value.trim();

    if (!editingOrigin.value.trim()) {
      alert('Origin cannot be empty.');
      return;
    }

    try {
      await updateApp(editingOrigin.clientId, { allowedOrigins: updatedOrigins });
      setEditingOrigin(null);
      load();
    } catch (err) {
      alert((err as Error).message || 'Failed to update origin.');
    }
  };

  const handleCancelEditOrigin = () => {
    setEditingOrigin(null);
  };

  const handleAddOrigin = async (clientId: string) => {
    const newOrigin = newOriginInput[clientId]?.trim();
    if (!newOrigin) return;

    const app = apps.find((a) => a.clientId === clientId);
    if (!app) return;

    const updatedOrigins = [...app.allowedOrigins, newOrigin];
    
    try {
      await updateApp(clientId, { allowedOrigins: updatedOrigins });
      setNewOriginInput((prev) => ({ ...prev, [clientId]: '' }));
      load();
    } catch (err) {
      alert((err as Error).message || 'Failed to add origin.');
    }
  };

  if (loading) return <div className="page-loader">Loading apps…</div>;

  if (loadError) return (
    <div className="apikeys-page">
      <div className="page-header">
        <div><h1>Apps</h1></div>
      </div>
      <div className="alert alert-error">
        <X size={16} />
        <span>{loadError}</span>
      </div>
    </div>
  );

  return (
    <div className="apikeys-page">
      <div className="page-header">
        <div>
          <h1>Apps</h1>
          <p>Each app has a unique Client ID used with <code>connectViaPortal(&#123; clientId &#125;)</code></p>
        </div>
        <button className="btn btn-accent" onClick={() => setShowCreate(true)}>
          <Plus size={16} /> New App
        </button>
      </div>

      {/* ── Create modal ─────────────────────────────────── */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2><AppWindow size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />Create App</h2>

            {createError && (
              <div className="alert alert-error">
                <X size={16} />
                <span>{createError}</span>
              </div>
            )}

            <div className="form-group">
              <label>App Name</label>
              <input
                placeholder="e.g. My Chat App"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>
                Allowed Origins
                <span className="optional"> (comma-separated)</span>
              </label>
              <input
                placeholder="https://myapp.com, http://localhost:3000"
                value={originsInput}
                onChange={(e) => setOriginsInput(e.target.value)}
              />
              <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginTop: 6 }}>
                The portal will only share tokens with these origins. Add your production and dev URLs.
              </p>
            </div>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowCreate(false)}>
                Cancel
              </button>
              <button
                className="btn btn-accent"
                onClick={handleCreate}
                disabled={creating || !appName.trim()}
              >
                {creating ? 'Creating…' : 'Create App'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Apps cards ───────────────────────────────────── */}
      {apps.length === 0 ? (
        <div className="empty-state" style={{ 
          padding: '48px 24px', 
          textAlign: 'center', 
          background: 'var(--surface)', 
          borderRadius: 8,
          border: '1px solid var(--border)'
        }}>
          No apps yet. Create one to get a Client ID for your app.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {apps.map((app) => (
            <div
              key={app.clientId}
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 20,
              }}
            >
              {/* Row 1: Name | Created */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 12
              }}>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: '1.1rem', 
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}>
                  {app.appName}
                </h3>
                <span style={{ 
                  fontSize: '0.85rem', 
                  color: 'var(--text-dim)',
                  whiteSpace: 'nowrap'
                }}>
                  Created {formatDate(app.createdAt)}
                </span>
              </div>

              {/* Row 2: Client ID */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: 'var(--text-dim)',
                  marginBottom: 6
                }}>
                  Client ID
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <code style={{ 
                    fontSize: '0.9rem',
                    padding: '6px 10px',
                    background: 'var(--surface-dim)',
                    borderRadius: 4,
                    flex: 1
                  }}>
                    {app.clientId}
                  </code>
                  <button
                    className="btn-icon"
                    title="Copy Client ID"
                    onClick={() => handleCopy(app.clientId)}
                  >
                    {copiedId === app.clientId ? (
                      <CheckCircle2 size={16} style={{ color: 'var(--success)' }} />
                    ) : (
                      <Copy size={16} />
                    )}
                  </button>
                </div>
              </div>

              {/* Row 3: Allowed Origins */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: 'var(--text-dim)',
                  marginBottom: 8
                }}>
                  Allowed Origins
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {app.allowedOrigins.length > 0 ? (
                    app.allowedOrigins.map((origin, index) => (
                      <div
                        key={`${app.clientId}-${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 12px',
                          background: 'var(--surface-dim)',
                          borderRadius: 4,
                          border: '1px solid var(--border)',
                          minHeight: '38px',
                        }}
                      >
                        {editingOrigin?.clientId === app.clientId && editingOrigin?.index === index ? (
                          <>
                            <input
                              type="text"
                              value={editingOrigin.value}
                              onChange={(e) => setEditingOrigin({ ...editingOrigin, value: e.target.value })}
                              style={{
                                flex: 1,
                                padding: '4px 8px',
                                fontSize: '0.85rem',
                                border: '1px solid var(--border)',
                                borderRadius: 4,
                                background: 'var(--surface)',
                                color: 'var(--text)',
                              }}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveEditOrigin();
                                if (e.key === 'Escape') handleCancelEditOrigin();
                              }}
                            />
                            <button
                              title="Save"
                              onClick={handleSaveEditOrigin}
                              style={{ 
                                color: 'var(--success)',
                                flexShrink: 0,
                                padding: 0,
                                width: '24px',
                                height: '24px',
                                minWidth: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                borderRadius: '4px',
                              }}
                            >
                              <Check size={16} />
                            </button>
                            <button
                              title="Cancel"
                              onClick={handleCancelEditOrigin}
                              style={{ 
                                flexShrink: 0,
                                padding: 0,
                                width: '24px',
                                height: '24px',
                                minWidth: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                borderRadius: '4px',
                              }}
                            >
                              <X size={16} />
                            </button>
                          </>
                        ) : (
                          <>
                            <span style={{ flex: 1, fontSize: '0.85rem', wordBreak: 'break-all' }}>
                              {origin}
                            </span>
                            <button
                              title="Edit origin"
                              onClick={() => handleStartEditOrigin(app.clientId, index, origin)}
                              style={{ 
                                flexShrink: 0,
                                padding: 0,
                                width: '24px',
                                height: '24px',
                                minWidth: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                color: 'var(--text)',
                              }}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              title="Delete origin"
                              onClick={() => handleDeleteOrigin(app.clientId, origin)}
                              style={{ 
                                flexShrink: 0,
                                padding: 0,
                                width: '24px',
                                height: '24px',
                                minWidth: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                color: 'var(--danger)',
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    ))
                  ) : (
                    <span style={{ color: 'var(--text-dim)', fontSize: '0.9rem', padding: '8px 0' }}>
                      No origins configured
                    </span>
                  )}
                  
                  {/* Add new origin */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 12px',
                      background: 'var(--surface-dim)',
                      borderRadius: 4,
                      border: '1px solid var(--border)',
                      minHeight: '38px',
                    }}
                  >
                    <input
                      type="text"
                      placeholder="https://example.com"
                      value={newOriginInput[app.clientId] || ''}
                      onChange={(e) => setNewOriginInput((prev) => ({ ...prev, [app.clientId]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newOriginInput[app.clientId]?.trim()) {
                          handleAddOrigin(app.clientId);
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: 0,
                        fontSize: '0.85rem',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--text)',
                        outline: 'none',
                        lineHeight: '1.5',
                      }}
                    />
                    <button
                      title="Add origin"
                      onClick={() => handleAddOrigin(app.clientId)}
                      disabled={!newOriginInput[app.clientId]?.trim()}
                      style={{
                        flexShrink: 0,
                        padding: 0,
                        width: '24px',
                        height: '24px',
                        minWidth: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'transparent',
                        border: 'none',
                        cursor: !newOriginInput[app.clientId]?.trim() ? 'not-allowed' : 'pointer',
                        borderRadius: '4px',
                        color: 'var(--text)',
                        opacity: !newOriginInput[app.clientId]?.trim() ? 0.5 : 1,
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                paddingTop: 12,
                borderTop: '1px solid var(--border)'
              }}>
                <button
                  className="btn btn-ghost"
                  title="Delete app"
                  onClick={() => handleDelete(app.clientId)}
                  style={{ 
                    color: 'var(--danger)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── SDK usage hint ───────────────────────────────── */}
      {apps.length > 0 && (
        <div className="alert alert-success" style={{ marginTop: 20 }}>
          <CheckCircle2 size={16} />
          <div>
            <strong>Using the SDK?</strong>
            <p style={{ marginTop: 4 }}>
              Pass the Client ID to <code>connectViaPortal()</code>:
            </p>
            <pre style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--text-dim)' }}>
{`import { connectViaPortal } from '@codetunezstudios/token-kit';

const token = await connectViaPortal({ clientId: '${apps[0].clientId}' });`}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
